import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toDateString } from '@/lib/format';
import { useCreateTransaction } from '@/hooks/useTransactions';
import {
  Plus,
  Loader2,
  CalendarIcon,
  TrendingUp,
  TrendingDown,
  FolderPlus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { z } from 'zod';
import type { Category } from '@/lib/api';

const transactionSchema = z.object({
  amount: z.number().positive('O valor deve ser maior que 0'),
  category_id: z.string().min(1, 'Selecione uma categoria'),
  date: z.string().min(1, 'Selecione uma data'),
  description: z.string().min(1, 'Descrição é obrigatória').max(200, 'Descrição muito longa'),
  type: z.enum(['income', 'expense'], { required_error: 'Selecione um tipo' }),
});

export interface TransactionFormDialogProps {
  categories: Category[];
}

export const TransactionFormDialog: React.FC<TransactionFormDialogProps> = ({ categories }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newTransaction, setNewTransaction] = useState({
    amount: '',
    category_id: '',
    date: toDateString(new Date()),
    description: '',
    type: '' as 'income' | 'expense' | '',
  });
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createTransaction = useCreateTransaction();

  const filteredCategories = newTransaction.type
    ? categories.filter((c) => c.type === newTransaction.type)
    : categories;

  const handleCreateTransaction = () => {
    setErrors({});

    const amount = parseFloat(newTransaction.amount);
    const validation = transactionSchema.safeParse({
      ...newTransaction,
      amount: isNaN(amount) ? 0 : amount,
    });

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    createTransaction.mutate(
      {
        amount,
        category_id: newTransaction.category_id,
        date: newTransaction.date,
        description: newTransaction.description,
        type: newTransaction.type as 'income' | 'expense',
      },
      {
        onSuccess: () => {
          setNewTransaction({
            amount: '',
            category_id: '',
            date: toDateString(new Date()),
            description: '',
            type: '',
          });
          setSelectedDate(new Date());
          setIsDialogOpen(false);
        },
      }
    );
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button className="gradient-primary text-primary-foreground">
          <Plus className="h-4 w-4 mr-2" />
          Nova Transação
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova Transação</DialogTitle>
          <DialogDescription>
            Registre uma nova receita ou despesa.
          </DialogDescription>
        </DialogHeader>

        {categories.length === 0 ? (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
              <FolderPlus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Nenhuma categoria ainda</h3>
              <p className="text-sm text-muted-foreground">
                Você precisa criar pelo menos uma categoria antes de adicionar transações.
              </p>
            </div>
            <Link to="/categories">
              <Button className="gradient-primary text-primary-foreground" onClick={() => setIsDialogOpen(false)}>
                <FolderPlus className="h-4 w-4 mr-2" />
                Criar Categoria
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <Select
                  value={newTransaction.type}
                  onValueChange={(value) => {
                    setNewTransaction({ ...newTransaction, type: value as 'income' | 'expense', category_id: '' });
                  }}
                >
                  <SelectTrigger className={errors.type ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-income" />
                        Receita
                      </div>
                    </SelectItem>
                    <SelectItem value="expense">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-expense" />
                        Despesa
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.type && <p className="text-xs text-destructive">{errors.type}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Valor</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newTransaction.amount}
                  onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                  className={errors.amount ? 'border-destructive' : ''}
                />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Select
                  value={newTransaction.category_id}
                  onValueChange={(value) => setNewTransaction({ ...newTransaction, category_id: value })}
                  disabled={!newTransaction.type}
                >
                  <SelectTrigger className={errors.category_id ? 'border-destructive' : ''}>
                    <SelectValue placeholder={newTransaction.type ? 'Selecione a categoria' : 'Selecione o tipo primeiro'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCategories.length === 0 && newTransaction.type ? (
                      <div className="py-4 px-2 text-center">
                        <p className="text-sm text-muted-foreground mb-2">
                          Nenhuma categoria de {newTransaction.type === 'income' ? 'receita' : 'despesa'}
                        </p>
                        <Link to="/categories" onClick={() => setIsDialogOpen(false)}>
                          <Button variant="outline" size="sm">
                            <FolderPlus className="h-3 w-3 mr-1" />
                            Criar uma
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      filteredCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.category_id && <p className="text-xs text-destructive">{errors.category_id}</p>}
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !selectedDate && 'text-muted-foreground',
                        errors.date && 'border-destructive'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setNewTransaction({ ...newTransaction, date: toDateString(date) });
                        }
                      }}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                {errors.date && <p className="text-xs text-destructive">{errors.date}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="What was this for?"
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                  className={errors.description ? 'border-destructive' : ''}
                />
                {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateTransaction}
                disabled={createTransaction.isPending}
                className="gradient-primary text-primary-foreground"
              >
                {createTransaction.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Transaction'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
